import { CheckoutRateAfterPollAttributes } from '~/reducers/orders/orders.reducer';
import { createSelector } from 'reselect';
import Order, {
  orderHasAmount,
  orderHasWeight,
  OrderSelection,
  type OrderTag,
} from '~/types/Order';
import { TransactionStatus, TransactionUI2 } from '~/types/Transaction';
import { handleRequestable } from '~/utils/fetchable';
import { ApplicationState } from '../store';
import { GridSelectionModel } from '@goshippo/components';

const EMPTY_ORDER = {
  partialFulfillmentRequests: [],
};

export const getOrdersStore = (state: ApplicationState) => state.orders;

export const getOrderIdFromProps = (_: unknown, props: { orderId: string }) => props.orderId;
export const getOrderObjectIdFromProps = (_: unknown, props: { orderObjectId: string }) =>
  props.orderObjectId;
export const getOrderObjectIdsFromProps = (
  _: unknown,
  props: { orderObjectIds: readonly string[] },
) => props.orderObjectIds;
export const getBatchEntryIdFromProps = (_: unknown, props: { batchEntryId: number }) =>
  props.batchEntryId;
export const getOrderSelectionFromProps = (_: unknown, props: { orderSelection: OrderSelection }) =>
  props.orderSelection;

export const getOrderAction = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.orderAction,
);

export const getIsSyncingOrders = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isSyncingOrders,
);

export const hasNewOrders = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.hasNewOrders,
);

export const getOrdersCount = createSelector(
  getOrdersStore,
  (orderStore) => orderStore.totalOrders,
);

export const getOrdersAllIds = createSelector(
  getOrdersStore,
  (ordersStore): readonly string[] => ordersStore.allIds,
);

export const getOrdersData = createSelector(
  getOrdersStore,
  (ordersStore): { [orderId: string]: Order } => ordersStore.data,
);

export const getOrders = createSelector(
  getOrdersAllIds,
  getOrdersData,
  (allIds, ordersData): Order[] => allIds.map((id) => ordersData[id]).filter((v) => v),
);

export const someOrderHasImportedWeight = createSelector(getOrdersData, (ordersData): boolean =>
  Object.values(ordersData).some((order) => orderHasWeight(order)),
);

export const someOrderHasImportedAmount = createSelector(getOrdersData, (ordersData): boolean =>
  Object.values(ordersData).some((order) => orderHasAmount(order)),
);

export const getTotalOrders = createSelector(
  getOrdersStore,
  (ordersStore): number => ordersStore.totalOrders,
);

export const getAllOrdersCount = createSelector(
  getOrdersStore,
  (ordersStore): number => ordersStore.allOrdersCount,
);

export const getIsLoadingOrdersCount = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isLoadingOrdersCount,
);

export const getHasOrderFetchError = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.fetchOrderHasError,
);

export const getIsOrderLoading = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isLoadingOrder,
);

export const getIsLoading = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isLoading,
);

export const getHideIsLoading = createSelector(getOrderAction, (orderAction): boolean => {
  switch (orderAction.kind) {
    case 'hide':
      return handleRequestable<null, string>(
        () => false,
        () => true,
        () => false,
        () => false,
      )(orderAction.request);
    default:
      return false;
  }
});

export const getOrderTransactions = createSelector(
  getOrdersStore,
  (ordersStore): HashMap<string, TransactionUI2[]> => ordersStore.transactions,
);

export const getPackingSlipUrl = createSelector(
  getOrdersStore,
  (ordersStore): string => ordersStore.packingSlipUrl,
);

export const getLabelUrl = createSelector(
  getOrdersStore,
  (ordersStore): string => ordersStore.labelDownloadUrl,
);

export const getLabelErrMsg = createSelector(
  getOrdersStore,
  (ordersStore): string => ordersStore.labelDownloadErrMsg!,
);

export const getIsDownloadingLabels = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isDownloadingLabels,
);

export const getNotificationsLoading = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.notificationsLoading,
);

export const getNotificationsSent = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.notificationsSent,
);

export const getLastOrderId = createSelector(
  getOrdersStore,
  (ordersStore): string => ordersStore.lastOrderId,
);

export const getSelectedOrders = createSelector(
  getOrdersStore,
  (ordersStore): OrderSelection => ordersStore.selectedOrders,
);

export const getCheckedOrders = createSelector(
  getOrdersStore,
  (ordersStore): OrderSelection => ordersStore.checkedOrders,
);

export const getRowModelBeingEdited = createSelector(
  getOrdersStore,
  (ordersStore): GridSelectionModel => ordersStore.rowModelBeingEdited,
);

export const getOrdersRemainingForLabelDownload = createSelector(
  getOrdersStore,
  (ordersStore): OrderSelection => ordersStore.ordersRemainingLabelDownload,
);

export const getCheckoutRateAfterPoll = createSelector(
  getOrdersStore,
  (ordersStore): Maybe<CheckoutRateAfterPollAttributes> => ordersStore.checkoutRateAfterPoll,
);

export const getSelectedOrdersData = createSelector(
  getSelectedOrders,
  getOrdersData,
  (ordersSelection, ordersData): Order[] =>
    Object.keys(ordersSelection)
      .map((id) => ordersData[id])
      .filter((v) => v),
);

export const getSelectedOrdersTags = createSelector(
  getSelectedOrdersData,
  (selectedOrdersData): OrderTag[] => {
    return selectedOrdersData.reduce<OrderTag[]>((acc, order) => {
      return acc.concat(order.tags || []);
    }, []);
  },
);

export const makeGetOrderById = () => {
  return createSelector(
    getOrdersData,
    getOrderIdFromProps,
    (ordersData, orderId: string): Order => ordersData[orderId],
  );
};

export const getOrderByObjectId = createSelector(
  getOrdersData,
  getOrderObjectIdFromProps,
  (ordersData, orderObjectId): Order => ordersData[orderObjectId] || EMPTY_ORDER,
);

export const makeGetIsOrderPartiallyFulfilled = () => {
  return createSelector(getOrderByObjectId, (order): boolean => {
    if (!order.partialFulfillmentRequests || !order.totalPartialFulfillments) {
      return false;
    }

    let hasFulfilledOrder = false;
    let hasUnfulfilledOrder = false;

    for (const request of order.partialFulfillmentRequests) {
      if (request.transactionObjectId) {
        const transactionStatus = order.transactions.find(
          (transaction) => transaction.objectId === request.transactionObjectId,
        )?.status;
        if (transactionStatus === TransactionStatus.SUCCESS) {
          hasFulfilledOrder = true;
        } else {
          hasUnfulfilledOrder = true;
        }
      } else {
        hasUnfulfilledOrder = true;
      }

      if (hasFulfilledOrder && hasUnfulfilledOrder) {
        return true;
      }
    }

    return false;
  });
};

export const getOrderById = createSelector(
  getOrdersData,
  getOrderIdFromProps,
  (ordersData, orderId: string): Order => ordersData[orderId],
);

export const orderHasImportedWeight = createSelector(getOrderById, (order): boolean =>
  orderHasWeight(order),
);

export const someSelectedOrderHasImportedAmount = createSelector(
  getSelectedOrdersData,
  (selectedOrdersData): boolean => selectedOrdersData.some((order) => orderHasAmount(order)),
);

export const someSelectedOrderHasImportedWeight = createSelector(
  getSelectedOrdersData,
  (selectedOrdersData): boolean => selectedOrdersData.some((order) => orderHasWeight(order)),
);

export const allSelectedOrdersHaveImportedWeight = createSelector(
  getSelectedOrdersData,
  (selectedOrdersData): boolean => selectedOrdersData.every((order) => orderHasWeight(order)),
);

export const getIsUpdatingOrder = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isUpdatingBatchEntry,
);

export const getIsSingleOrderMode = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isSingleOrderMode,
);

export const getIsOnOrderTableScreen = createSelector(
  getOrdersStore,
  (ordersStore): boolean => ordersStore.isOnOrdersTableScreen,
);

export const getIsOrderSelected = createSelector(
  getSelectedOrders,
  getOrderObjectIdFromProps,
  (selectedOrders, orderObjectId: string): boolean => {
    return !!selectedOrders[orderObjectId];
  },
);

export const getAreAllOrdersSelected = createSelector(
  getSelectedOrders,
  getOrdersAllIds,
  (selectedOrders, allIds): boolean => Object.keys(selectedOrders).length === allIds.length,
);

export const getIsBatchEntrySelected = createSelector(
  getSelectedOrders,
  (_: unknown, props: { orderObjectId: string; batchEntryId: number }) => props,
  (selectedOrders, props): boolean =>
    (selectedOrders[props.orderObjectId] || []).includes(props.batchEntryId),
);

export const getSelectedCount = createSelector(getSelectedOrders, (selectedOrders): number => {
  return Object.values(selectedOrders).reduce((count, currVal) => {
    return currVal.length ? count + currVal.length : count + 1;
  }, 0);
});

export const getBatchEntryEditNotificationState = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.batchEntryEditNotificationState,
);

export const getPatchBatchEntriesExtrasRequest = createSelector(
  getOrdersStore,
  (orderStore) => orderStore.patchBatchEntriesExtrasRequest,
);

export const getOrdersDataByObjectIds = createSelector(
  getOrdersData,
  getOrderObjectIdsFromProps,
  (ordersData, orderObjectIds) => {
    return orderObjectIds.map((objectId) => ordersData[objectId]);
  },
);

// For input of order object IDs, returns an array of the latest successful transaction
// for each applicable order
export const getOrderSuccessfulTransactionIds = createSelector(
  getOrdersDataByObjectIds,
  (ordersData) => {
    // filter on orders that have successful transactions
    const ordersWithTransactions = ordersData.filter((order) => {
      return order?.transactions?.some((transaction) => transaction.status === 'SUCCESS');
    });

    return ordersWithTransactions.map((order) => {
      const transactions = order.transactions.filter((t) => t.status === 'SUCCESS');
      // objectCreated/Updated are not populated, cannot sort by date, must assume the first transaction in list
      return transactions[0].objectId;
    });
  },
);

export const getIsUpdatingCustomsItems = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isUpdatingCustomsItems,
);

export const getIsCreatingCustomsItems = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isCreatingCustomsItems,
);

export const getIsSidePanelCheckout = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isSidePanelCheckout,
);

export const getIsParcelUpdating = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isUpdatingParcel,
);

export const getAutoSaveErrors = (orderSelection: OrderSelection) =>
  createSelector(getOrdersStore, (ordersStore) => {
    return !!Object.keys(orderSelection).find((orderId) => ordersStore.autoSaveErrors[orderId]);
  });

export const getIsLoadingOrderTags = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.isLoadingOrderTags,
);

export const getFetchOrderTagsHasError = createSelector(
  getOrdersStore,
  (ordersStore) => ordersStore.fetchOrderTagsHasError,
);
